import * as THREE from 'three';
import { clamp, lerp, smoothstep } from '@/core/rng';

/**
 * The armed verb's footprint, laid on the land under the cursor.
 *
 * A verb is a promise about an area, and under Divine Focus that area is the
 * zoom: the reticle is how the player reads their own attention before they
 * spend it. Small, bright and hard-edged when the eye is close — this will be
 * one tree. Wide, dim and feathered when the eye is high — this will be
 * weather, and it will not be precise. On cast it leaves an expanding pulse
 * at the true radius. Render-only and stateless from the simulation's point
 * of view: main.ts feeds it a point, a verb and a focus.
 */

export type ReticleVerb = 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen';

/** What the player's zoom means, as the reticle needs to draw it. */
export interface ReticleFocus {
  /** True cast radius in metres — the same number the simulation will use. */
  radius: number;
  /** 0 crisp and defined .. 1 wide and diffuse. */
  softness: number;
  /** Camera orbit distance: keeps a tight rim a few pixels wide, not a hair. */
  camDistance: number;
  /**
   * How charged the held cast is, 0..1 (stones gathered over the five a god
   * holds; 0 when nothing is held). A charged aim burns: the rim goes to full,
   * the fill and the haze come up, the breath quickens, the dashes hurry —
   * so the hand can feel the stones gathering before the key comes up.
   */
  charge?: number;
}

const TINT: Record<ReticleVerb, number> = {
  strike: 0xffd27a,
  rain: 0x8fc3e0,
  strengthen: 0xa4d68e,
  weaken: 0xc9a06a,
  // a sign leaves no mark on the land; the gold is the god's own knowledge
  // of where it aimed, which is more than any mortal will ever have
  omen: 0xe8d8a0,
};

/** The rim's width, in fractions of the radius: a line, then a band. */
const RIM_THIN = 0.011, RIM_WIDE = 0.075;
/** …but never thinner than this fraction of the camera distance (≈3–4 px). */
const RIM_MIN_SCREEN = 0.0055;
/** The rim geometry is rebuilt when its inner radius moves this far. */
const RIM_STEP = 0.004;

/** A scoop being taken: where the hole is, where the earth is going. */
export interface DigAim {
  /** The pit, frozen at the moment the ground was taken hold of. */
  pit: { x: number; y: number; z: number; r: number };
  /** Where the earth would land if the hand opened now — null while unaimed. */
  drop: { x: number; y: number; z: number; r: number } | null;
  /** 0 at the first instant of the hold, 1 at the ground's own repose angle. */
  depth: number;
  /** False until the hold has dug something the island would notice. */
  enough: boolean;
}

export interface Reticle {
  group: THREE.Group;
  /** Follow the cursor: a verb, a ground point and a focus, or null to hide. */
  aim(verb: ReticleVerb | null, p: THREE.Vector3 | null, focus: ReticleFocus): void;
  /**
   * A hand in the ground: the hole being opened and the ground it is bound
   * for. Null hides it.
   *
   * Separate from `aim` because a dig is a faculty and not a verb — `aim` is
   * driven by `hud.armed`, and synthesising a sixth ReticleVerb to borrow its
   * rings would fight that. It draws only: while the key is held the world has
   * not changed at all, and law 9 says a gesture in progress writes nothing,
   * anywhere.
   */
  dig(a: DigAim | null): void;
  /** The cast happened here, this wide: leave an expanding pulse behind. */
  pulse(verb: ReticleVerb, p: THREE.Vector3, radius: number): void;
  update(tSec: number, dt: number): void;
}

/** Flat, unlit, never buried by a hillside — an aim must read from any angle. */
function flatMaterial(opacity: number, vertexColors = false): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    // no depth test: a flat ring on sloped ground would bury its far arc in
    // the hillside, and half a footprint is a lie about where the verb lands
    transparent: true, opacity, depthWrite: false, depthTest: false,
    side: THREE.DoubleSide, vertexColors,
  });
}

export function createReticle(): Reticle {
  const group = new THREE.Group();

  const mkRing = (inner: number, opacity: number) => {
    const m = new THREE.Mesh(
      new THREE.RingGeometry(inner, 1, 72).rotateX(-Math.PI / 2),
      flatMaterial(opacity),
    );
    m.renderOrder = 5;
    m.visible = false;
    return m;
  };

  // the aim: a rim whose width answers the focus, a whisper of a fill, a
  // slow-turning dash ring, a haze that only appears when attention spreads,
  // and a centre mark that only survives when attention narrows
  const rim = mkRing(1 - RIM_THIN, 0.9);
  let rimInner = 1 - RIM_THIN;
  const fill = mkRing(0, 0.05);

  /**
   * The diffuse half of the reticle: a wide annulus whose alpha ramps to a
   * peak at the true radius and dies at both edges, so a watershed cast has
   * no edge to read as a wall — it fades out the way weather does. Vertex
   * alpha carries the ramp; the material's colour carries the verb's tint,
   * because Three multiplies the two.
   */
  const haze = (() => {
    const g = new THREE.RingGeometry(0.55, 1.42, 84, 7).rotateX(-Math.PI / 2);
    const pos = g.attributes.position;
    const col = new Float32Array(pos.count * 4);
    for (let i = 0; i < pos.count; i++) {
      const r = Math.hypot(pos.getX(i), pos.getZ(i));
      const a = r < 1 ? smoothstep(0.55, 1, r) : 1 - smoothstep(1, 1.42, r);
      col[i * 4] = col[i * 4 + 1] = col[i * 4 + 2] = 1;
      col[i * 4 + 3] = a * a;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 4));
    const m = new THREE.Mesh(g, flatMaterial(0, true));
    m.renderOrder = 4;
    m.visible = false;
    return m;
  })();

  const dashes = (() => {
    // 24 short arcs, drawn as one geometry, rotating slowly inside the rim
    const segs: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 24; i++) {
      const g = new THREE.RingGeometry(0.90, 0.925, 6, 1,
        (i / 24) * Math.PI * 2, (0.6 / 24) * Math.PI * 2);
      segs.push(g);
    }
    const merged = new THREE.BufferGeometry();
    const arrays = segs.map((g) => ({
      p: g.attributes.position.array as Float32Array,
      i: [...(g.index!.array as Uint16Array)],
    }));
    let vtx = 0, idxOff = 0;
    for (const a of arrays) vtx += a.p.length;
    const pos = new Float32Array(vtx);
    const idx: number[] = [];
    let o = 0;
    for (const a of arrays) {
      pos.set(a.p, o);
      for (const i of a.i) idx.push(i + idxOff);
      idxOff += a.p.length / 3;
      o += a.p.length;
    }
    merged.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    merged.setIndex(idx);
    merged.rotateX(-Math.PI / 2);
    segs.forEach((g) => g.dispose());
    const m = new THREE.Mesh(merged, flatMaterial(0.55));
    m.renderOrder = 5;
    m.visible = false;
    return m;
  })();

  // the point of aim itself: at Presence the disc is a few metres across and
  // the rim alone reads as a dot, so a bright pip says "this, exactly this"
  const pip = (() => {
    const m = new THREE.Mesh(
      new THREE.CircleGeometry(1, 20).rotateX(-Math.PI / 2), flatMaterial(0.9));
    m.renderOrder = 6;
    m.visible = false;
    return m;
  })();

  /**
   * The dig: a hole opening and a place for it to go.
   *
   * Four meshes and no new material ideas — the pit is a disc that darkens as
   * the hold deepens, ringed so its edge is readable on any ground; the drop
   * is an open ring in the same earth tone, lighter, because nothing is there
   * yet. Both are flat, both ignore depth like every other reticle mesh, so
   * neither can be half-buried by the hillside it is describing.
   */
  const PIT_TINT = 0x8a6a41, DROP_TINT = 0xd6bd91;
  const pitFill = (() => {
    const m = new THREE.Mesh(
      new THREE.CircleGeometry(1, 64).rotateX(-Math.PI / 2), flatMaterial(0.3));
    m.renderOrder = 5; m.visible = false;
    return m;
  })();
  const pitRim = mkRing(0.955, 0.85);
  const dropRim = mkRing(0.975, 0.55);
  // the one cue that says which way the earth is going: a short chevron of
  // dashes on the drop ring, spun by update() so it reads as arriving
  const dropSpin = mkRing(0.88, 0.35);

  group.add(haze, fill, rim, dashes, pip, pitFill, pitRim, dropRim, dropSpin);

  // pulses: a small pool of expanding rings, recycled oldest-first. Each
  // remembers the radius it was cast at — a watershed pulse is a watershed
  // wide, which is most of what sells the scale of what just happened
  interface Pulse { mesh: THREE.Mesh; born: number; radius: number; alive: boolean }
  const pulses: Pulse[] = Array.from({ length: 3 }, () => {
    const mesh = mkRing(0.96, 0.8);
    group.add(mesh);
    return { mesh, born: 0, radius: 1, alive: false };
  });
  let clockNow = 0;

  // live focus, remembered between aim() and update()
  let radius = 420, softness = 0.5, breath = 0.01, breathRate = 1.6, charged = 0;

  const WHITE = new THREE.Color(0xffffff);
  const setColor = (mesh: THREE.Mesh, verb: ReticleVerb, whiten = 0) => {
    const m = mesh.material as THREE.MeshBasicMaterial;
    m.color.setHex(TINT[verb]);
    if (whiten > 0) m.color.lerp(WHITE, whiten);
  };

  return {
    group,
    aim(verb, p, focus) {
      const on = verb !== null && p !== null;
      rim.visible = fill.visible = dashes.visible = on;
      haze.visible = on && focus.softness > 0.02;
      pip.visible = on && focus.softness < 0.75;
      if (!on || verb === null || p === null) return;

      radius = Math.max(0.5, focus.radius);
      softness = clamp(focus.softness, 0, 1);

      // the rim narrows to a bright hairline as attention narrows, but never
      // below a few pixels: a 5 m circle drawn 6 cm thick is not a reticle
      const wantWidth = Math.max(radius * lerp(RIM_THIN, RIM_WIDE, softness),
                                 focus.camDistance * RIM_MIN_SCREEN);
      const inner = clamp(1 - wantWidth / radius, 0.5, 0.994);
      if (Math.abs(inner - rimInner) > RIM_STEP) {
        rimInner = inner;
        rim.geometry.dispose();
        rim.geometry = new THREE.RingGeometry(inner, 1, 72).rotateX(-Math.PI / 2);
      }

      // defined and bright, or soft and barely there — and burning, if a
      // held key is gathering stones into it (2026-08-18)
      const ch = clamp(focus.charge ?? 0, 0, 1);
      charged = ch;
      (rim.material as THREE.MeshBasicMaterial).opacity = Math.min(1, lerp(0.95, 0.30, softness) + ch * 0.6);
      (fill.material as THREE.MeshBasicMaterial).opacity = lerp(0.030, 0.085, softness) + ch * 0.16;
      (dashes.material as THREE.MeshBasicMaterial).opacity = Math.min(1, lerp(0.62, 0.0, softness) + ch * 0.5);
      (haze.material as THREE.MeshBasicMaterial).opacity = Math.min(0.8, softness * 0.34 + ch * 0.45);
      haze.visible = haze.visible || ch > 0.02;
      (pip.material as THREE.MeshBasicMaterial).opacity = lerp(0.85, 0, smoothstep(0.15, 0.75, softness));
      // wide attention breathes slowly and loosely; tight attention holds still
      // — and a charge breathes hard and fast
      breath = lerp(0.004, 0.020, softness) + ch * 0.05;
      breathRate = lerp(2.4, 0.85, softness) * (1 + ch * 3);

      const y = p.y + 1.2;
      for (const mesh of [rim, fill, dashes, haze]) {
        setColor(mesh, verb, (1 - softness) * 0.30);
        mesh.position.set(p.x, y, p.z);
        mesh.scale.setScalar(radius);
      }
      haze.position.y = y - 0.05;
      setColor(pip, verb, 0.55);
      pip.position.set(p.x, y + 0.1, p.z);
      pip.scale.setScalar(Math.max(radius * 0.05, focus.camDistance * 0.007));
    },
    dig(a) {
      const on = a !== null;
      pitFill.visible = pitRim.visible = on;
      dropRim.visible = dropSpin.visible = on && a?.drop !== null;
      if (!a) return;

      // The hold reads as the hole getting darker, which is the only thing a
      // flat ring can honestly say about depth from above. `enough` is the
      // island's own median slope arriving as a threshold (tools/handful.ts):
      // under it the gesture has made nothing, and the pale ring says so
      // before the release does.
      const t = clamp(a.depth, 0, 1);
      const mat = pitFill.material as THREE.MeshBasicMaterial;
      mat.color.setHex(PIT_TINT);
      // 0.34 at the bottom of the hold, not 0.52. Photographed at 0.52 it read
      // as a disc of paint laid on the meadow rather than as ground about to
      // go — which is precisely the failure the promotion ground-mark hit, and
      // it is worth being twice shy about. The rim carries the edge; the fill
      // only has to say "this much".
      mat.opacity = lerp(0.05, 0.34, t);
      const rimMat = pitRim.material as THREE.MeshBasicMaterial;
      rimMat.color.setHex(PIT_TINT);
      if (!a.enough) rimMat.color.lerp(WHITE, 0.55);
      rimMat.opacity = a.enough ? 0.9 : 0.45;

      const y = a.pit.y + 1.2;
      pitFill.position.set(a.pit.x, y, a.pit.z);
      pitFill.scale.setScalar(a.pit.r);
      pitRim.position.set(a.pit.x, y + 0.1, a.pit.z);
      pitRim.scale.setScalar(a.pit.r);

      if (a.drop) {
        for (const m of [dropRim, dropSpin]) {
          (m.material as THREE.MeshBasicMaterial).color.setHex(DROP_TINT);
          m.position.set(a.drop.x, a.drop.y + 1.2, a.drop.z);
          m.scale.setScalar(a.drop.r);
        }
        (dropRim.material as THREE.MeshBasicMaterial).opacity = lerp(0.20, 0.70, t);
        (dropSpin.material as THREE.MeshBasicMaterial).opacity = lerp(0.05, 0.30, t);
      }
    },
    pulse(verb, p, r) {
      const slot = pulses.find((s) => !s.alive) ??
        pulses.reduce((a, b) => (a.born <= b.born ? a : b));
      slot.alive = true;
      slot.born = clockNow;
      slot.radius = Math.max(0.5, r);
      setColor(slot.mesh, verb);
      slot.mesh.position.set(p.x, p.y + 1.2, p.z);
      slot.mesh.visible = true;
    },
    update(tSec, _dt) {
      clockNow = tSec;
      // the dashes hurry when the focus is tight and drift when it is wide —
      // and race while a charge gathers
      dashes.rotation.y = tSec * lerp(0.34, 0.07, softness) * (1 + charged * 6);
      // the drop ring turns the other way from everything else, so that even
      // in a still frame it is obvious which of the two circles is the target
      if (dropSpin.visible) dropSpin.rotation.y = -tSec * 0.5;
      // the aim breathes, faintly — alive in the hand
      const b = 1 + Math.sin(tSec * breathRate) * breath;
      if (rim.visible) {
        rim.scale.setScalar(radius * b);
        fill.scale.setScalar(radius * b);
        haze.scale.setScalar(radius * (1 + (b - 1) * 1.8));
      }
      for (const s of pulses) {
        if (!s.alive) continue;
        // a wide cast takes longer to arrive than a narrow one
        const life = lerp(0.75, 1.9, smoothstep(60, 3000, s.radius));
        const age = (tSec - s.born) / life;
        if (age >= 1) { s.alive = false; s.mesh.visible = false; continue; }
        s.mesh.scale.setScalar(s.radius * (1 + age * 0.35));
        (s.mesh.material as THREE.MeshBasicMaterial).opacity = 0.8 * (1 - age) * (1 - age);
      }
    },
  };
}
